import React, { useState, useEffect, useCallback } from 'react';
import { FiFileText } from "react-icons/fi";
import { LuUser } from "react-icons/lu";
import './NeuroAssessmentForm.css';
import Select from "react-select";
import { makeApiCall } from './AssessmentCall';
import { useAlert } from 'react-alert';
import Brunnstrom from './Brunnstorm';
import PainScale from './PainScale';
import NewScoring from './NewScoring';
import { LuExpand, LuShrink } from "react-icons/lu";
import { IoPrintOutline } from "react-icons/io5";
import AssessmentPreview from './NeuroAssessmentPreview';
const NeuroAssessment = ({ data, editData = null, isEditing = false, onSubmit }) => {
  const [loading, setLoading] = useState(false);
  const alert = useAlert();
  const [refresh, setRefresh] = useState(false)
  const [showPreview, setShowPreview] = useState({
    show: false,
    showSubmitButton: false,
    onSubmit: () => { }
  });
  const [show, setShow] = useState(true)
  const muscleGroups = [
    { key: 'shoulderFlexion', label: 'Shoulder Flexion' },
    { key: 'shoulderExtension', label: 'Shoulder Extension' },
    { key: 'shoulderAbduction', label: 'Shoulder Abduction' },
    { key: 'shoulderAdduction', label: 'Shoulder Adduction' },
    { key: 'elbowFlexion', label: 'Elbow Flexion' },
    { key: 'elbowExtension', label: 'Elbow Extension' },
    { key: 'wristFlexion', label: 'Wrist Flexion' },
    { key: 'wristExtension', label: 'Wrist Extension' },
    { key: 'hipFlexion', label: 'Hip Flexion' },
    { key: 'hipExtension', label: 'Hip Extension' },
    { key: 'hipAbduction', label: 'Hip Abduction' },
    { key: 'hipAdduction', label: 'Hip Adduction' },
    { key: 'kneeFlexion', label: 'Knee Flexion' },
    { key: 'kneeExtension', label: 'Knee Extension' },
    { key: 'ankleDorsiflexion', label: 'Ankle Dorsiflexion' },
    { key: 'anklePlantarflexion', label: 'Ankle Plantarflexion' }
  ];

  // Initialize motor function data
  const initialMotorFunction = muscleGroups.map(muscle => ({
    muscle: muscle.label,
    side: 'RT',
    mmt: '',
    rom: '',
    tone: '',
    proximal: '',
    limb_length: '',
    muscle_tone: '',
    girth_measure: '',
    capsular_pattern: '',
    resisted_isometric_test: ''
  }));

  const [formData, setFormData] = useState({
    therapy_consultation: data?.therapy_id || '',
    id: data?.id || '',
    name: data?.name || '',
    age: data?.age || '',
    gender: data?.gender || '',
    contact_number: data?.mob || '',
    hand_dominance: 'Right',
    chief_complaints: '',
    chiefComplaint1: '',
    chiefComplaint2: '',
    chiefComplaint3: '',
    chiefComplaint4: '',
    chiefComplaint5: '',
    history: {
      present: '',
      past: '',
      medical: '',
      surgical: ''
    },
    general_examination: {
      pulse: '',
      bp: ''
    },
    built: 'moderate',
    conscious: true,
    alert: true,
    obeys: true,
    oriented: true,
    speech: 'clear',
    cne_examination: '',
    motor_function: initialMotorFunction,
    reflexes: [
      { reflex_type: 'Biceps Jerk', side: 'Right', response: '' },
      { reflex_type: 'Biceps Jerk', side: 'Left', response: '' },
      { reflex_type: 'Supinator Jerk', side: 'Right', response: '' },
      { reflex_type: 'Supinator Jerk', side: 'Left', response: '' },
      { reflex_type: 'Triceps Jerk', side: 'Right', response: '' },
      { reflex_type: 'Triceps Jerk', side: 'Left', response: '' },
      { reflex_type: 'Knee Jerk', side: 'Right', response: '' },
      { reflex_type: 'Knee Jerk', side: 'Left', response: '' },
      { reflex_type: 'Ankle Jerk', side: 'Right', response: '' },
      { reflex_type: 'Ankle Jerk', side: 'Left', response: '' }
    ],
    babinski: false,
    babinski_details: '',
    sensory: {
      superficial: {
        fineTouch: { RT: '', LT: '' },
        pain: { RT: '', LT: '' },
        temperature: { RT: '', LT: '' }
      },
      deep: {
        jointPosition: { RT: '', LT: '' },
        jointMovement: { RT: '', LT: '' },
        vibration: { RT: '', LT: '' }
      },
      cortical: {
        stereognosis: { RT: '', LT: '' },
        twoPointDiscrimination: { RT: '', LT: '' },
        tactileLocalization: { RT: '', LT: '' }
      }
    },
    coordination: {
      finger_to_nose: '',
      finger_opposition: '',
      mass_grasp: '',
      pronation_supination: '',
      rebound_test: '',
      tapping_hand: '',
      tapping_foot: '',
      heel_to_knee: '',
      drawing_circle_hand: '',
      drawing_circle_foot: ''
    },
    balance: {
      sitting: '',
      standing: ''
    },
    posture: '',
    gait: '',
    functional_diagnosis: '',
    treatment_goal: '',
    brunnstorm: {
      admission: '1',
      discharge: '1'
    },
    pain_rating: {
      admission: null,
      discharge: null
    },
    difficultyWalking: false,
    difficultyADL: false,
    painPresent: '',
    otherComplaints: '',
  });

  // Initialize form with edit data if in edit mode
  useEffect(() => {
    if (isEditing && editData) {
      const transformedData = transformApiData(editData);
      setFormData(transformedData);
    }
  }, [isEditing, editData]);

  // Transform API response to match form structure
  const transformApiData = (apiData) => {
    // Convert motor_function array to our format
    const motorFunction = apiData.motor_function?.length > 0
      ? apiData.motor_function
      : initialMotorFunction;
    const complaintsArray = apiData.chief_complaints?.split(',') || [];
    return {
      ...formData,
      therapy_consultation: apiData.therapy_consultation || '',
      id: data?.id || '',
      name: data?.name || '',
      age: data?.age || '',
      gender: data?.gender || '',
      contact_number: data?.mob || '',
      hand_dominance: apiData.hand_dominance || 'Right',
      chief_complaints: apiData.chief_complaints || '',
      chiefComplaint1: complaintsArray[0]?.trim() || '',
      chiefComplaint2: complaintsArray[1]?.trim() || '',
      chiefComplaint3: complaintsArray[2]?.trim() || '',
      chiefComplaint4: complaintsArray[3]?.trim() || '',
      chiefComplaint5: complaintsArray[4]?.trim() || '',
      history: {
        present: apiData.history?.present || '',
        past: apiData.history?.past || '',
        medical: apiData.history?.medical || '',
        surgical: apiData.history?.surgical || ''
      },
      general_examination: {
        pulse: apiData.general_examination?.pulse || '',
        bp: apiData.general_examination?.bp || ''
      },
      built: apiData.built || 'moderate',
      conscious: apiData.conscious ?? true,
      alert: apiData.alert ?? true,
      obeys: apiData.obeys ?? true,
      oriented: apiData.oriented ?? true,
      speech: apiData.speech || 'clear',
      cne_examination: apiData.cne_examination || '',
      motor_function: motorFunction,
      reflexes: apiData.reflexes || formData.reflexes,
      babinski: apiData.babinski || false,
      babinski_details: apiData.babinski_details || '',
      sensory: apiData.sensory || formData.sensory,
      coordination: apiData.coordination || formData.coordination,
      balance: apiData.balance || { sitting: '', standing: '' },
      posture: apiData.posture || '',
      gait: apiData.gait || '',
      functional_diagnosis: apiData.functional_diagnosis || '',
      treatment_goal: apiData.treatment_goal || '',
      brunnstorm: apiData.brunnstorm || { admission: '1', discharge: '1' },
      pain_rating: apiData.pain_rating || { admission: null, discharge: null },
      motor_function: motorFunction
    };
  };

  // Handlers
  const handleInputChange = useCallback((field, value) => {
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }));
    } else {
      setFormData(prev => ({ ...prev, [field]: value }));
    }
  }, []);

  const handleNestedChange = useCallback((section, field, subfield, value) => {
    setFormData(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: {
          ...prev[section][field],
          [subfield]: value
        }
      }
    }));
  }, []);

  const handleReflexChange = useCallback((index, field, value) => {
    setFormData(prev => {
      const updatedReflexes = [...prev.reflexes];
      updatedReflexes[index] = {
        ...updatedReflexes[index],
        [field]: value
      };
      return {
        ...prev,
        reflexes: updatedReflexes
      };
    });
  }, []);

  // Handle motor function changes
  const handleMotorFunctionChange = useCallback((index, field, value, side = null) => {
    setFormData(prev => {
      const updatedMotorFunction = [...prev.motor_function];

      if (side) {
        // Find or create entry for this muscle and side
        let entryIndex = updatedMotorFunction.findIndex(
          mf => mf.muscle === muscleGroups[index].label && mf.side === side
        );

        if (entryIndex === -1) {
          // Create new entry if not found
          updatedMotorFunction.push({
            muscle: muscleGroups[index].label,
            side: side,
            mmt: '',
            rom: '',
            tone: '',
            proximal: '',
            limb_length: '',
            muscle_tone: '',
            girth_measure: '',
            capsular_pattern: '',
            resisted_isometric_test: ''
          });
          entryIndex = updatedMotorFunction.length - 1;
        }

        updatedMotorFunction[entryIndex] = {
          ...updatedMotorFunction[entryIndex],
          [field]: value
        };
      } else {
        // Update existing entry
        updatedMotorFunction[index] = {
          ...updatedMotorFunction[index],
          [field]: value
        };
      }

      return {
        ...prev,
        motor_function: updatedMotorFunction
      };
    });
  }, []);

  const getMotorFunctionValue = (muscleName, side, field) => {
    const entry = formData.motor_function.find(
      mf => mf.muscle === muscleName && mf.side === side
    );
    return entry ? entry[field] || '' : '';
  };

  const handleCancel = () => {
    window.scrollTo({
      top: 10,
      behavior: 'smooth'
    });
    if (isEditing && editData) {
      setFormData(transformApiData(editData));
    } else {
      setRefresh(!refresh)
      setFormData({
        ...formData,
        therapy_consultation: data?.therapy_id || '',
        id: data?.id || '',
        name: data?.name || '',
        age: data?.age || '',
        gender: data?.gender || '',
        contact_number: data?.mob || '',
        hand_dominance: 'Right',
        chief_complaints: '',
        chiefComplaint1: '',
        chiefComplaint2: '',
        chiefComplaint3: '',
        chiefComplaint4: '',
        chiefComplaint5: '',
        history: {
          present: '',
          past: '',
          medical: '',
          surgical: ''
        },
        general_examination: {
          pulse: '',
          bp: ''
        },
        built: 'moderate',
        conscious: true,
        alert: true,
        obeys: true,
        oriented: true,
        speech: 'clear',
        cne_examination: '',
        motor_function: initialMotorFunction,
        reflexes: [
          { reflex_type: 'Biceps Jerk', side: 'Right', response: '' },
          { reflex_type: 'Biceps Jerk', side: 'Left', response: '' },
          { reflex_type: 'Supinator Jerk', side: 'Right', response: '' },
          { reflex_type: 'Supinator Jerk', side: 'Left', response: '' },
          { reflex_type: 'Triceps Jerk', side: 'Right', response: '' },
          { reflex_type: 'Triceps Jerk', side: 'Left', response: '' },
          { reflex_type: 'Knee Jerk', side: 'Right', response: '' },
          { reflex_type: 'Knee Jerk', side: 'Left', response: '' },
          { reflex_type: 'Ankle Jerk', side: 'Right', response: '' },
          { reflex_type: 'Ankle Jerk', side: 'Left', response: '' }
        ],
        babinski: false,
        babinski_details: '',
        sensory: {
          superficial: {
            fineTouch: { RT: '', LT: '' },
            pain: { RT: '', LT: '' },
            temperature: { RT: '', LT: '' }
          },
          deep: {
            jointPosition: { RT: '', LT: '' },
            jointMovement: { RT: '', LT: '' },
            vibration: { RT: '', LT: '' }
          },
          cortical: {
            stereognosis: { RT: '', LT: '' },
            twoPointDiscrimination: { RT: '', LT: '' },
            tactileLocalization: { RT: '', LT: '' }
          }
        },
        coordination: {
          finger_to_nose: '',
          finger_opposition: '',
          mass_grasp: '',
          pronation_supination: '',
          rebound_test: '',
          tapping_hand: '',
          tapping_foot: '',
          heel_to_knee: '',
          drawing_circle_hand: '',
          drawing_circle_foot: ''
        },
        balance: {
          sitting: '',
          standing: ''
        },
        posture: '',
        gait: '',
        functional_diagnosis: '',
        treatment_goal: '',
        brunnstorm: {
          admission: '1',
          discharge: '1'
        },
        pain_rating: {
          admission: null,
          discharge: null
        },
        difficultyWalking: false,
        difficultyADL: false,
        painPresent: '',
        otherComplaints: ''
      });
    }
  };
  const validateForm = () => {
    const combinedComplaints = [
      formData.chiefComplaint1,
      formData.chiefComplaint2,
      formData.chiefComplaint3,
      formData.chiefComplaint4,
      formData.chiefComplaint5
    ].filter(complaint => complaint.trim() !== '').join(', ');

    if (!combinedComplaints || combinedComplaints === "") {
      alert.error("Add chief complaint");
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    setShowPreview({
      show: false,
      showSubmitButton: false,
      onSubmit: () => { }
    });
    setLoading(true);

    const combinedComplaints = [
      formData.chiefComplaint1,
      formData.chiefComplaint2,
      formData.chiefComplaint3,
      formData.chiefComplaint4,
      formData.chiefComplaint5
    ].filter(complaint => complaint.trim() !== '').join(', ');

    const formattedMotorFunction = formData.motor_function.map(mf => ({
      muscle: mf.muscle,
      side: mf.side,
      mmt: mf.mmt,
      rom: mf.rom,
      tone: mf.tone,
      proximal: mf.proximal,
      limb_length: mf.limb_length,
      muscle_tone: mf.muscle_tone,
      girth_measure: mf.girth_measure,
      capsular_pattern: mf.capsular_pattern,
      resisted_isometric_test: mf.resisted_isometric_test
    }));

    const formattedData = {
      patient: +formData.id,
      assessment_form: "neuro",
      therapy_consultation: formData.therapy_consultation,
      hand_dominance: formData.hand_dominance,
      chief_complaints: combinedComplaints,
      history: formData.history,
      general_examination: formData.general_examination,
      built: formData.built || 'moderate',
      conscious: formData.conscious,
      alert: formData.alert,
      obeys: formData.obeys,
      oriented: formData.oriented,
      speech: formData.speech,
      cne_examination: formData.cne_examination || null,
      motor_function: formattedMotorFunction,
      reflexes: formData.reflexes.filter(reflex => reflex.response !== ''),
      babinski: formData.babinski,
      ...(formData.babinski && { babinski_details: formData.babinski_details }),
      sensory: formData.sensory,
      coordination: formData.coordination,
      balance: formData.balance,
      posture: formData.posture,
      gait: formData.gait,
      functional_diagnosis: formData.functional_diagnosis,
      treatment_goal: formData.treatment_goal,
      brunnstorm: formData.brunnstorm,
      pain_rating: formData.pain_rating
    };

    try {
      const endpoint = isEditing
        ? `/api/v1/patient/therapy-assessment/?assessment_form=neuro&assessment_id=${editData.id}`
        : '/api/v1/patient/therapy-assessment/';

      const method = isEditing ? 'put' : 'post';

      const result = await makeApiCall({
        method,
        endpoint,
        data: formattedData,
      });

      if (result.success) {
        alert.success(`Assessment ${isEditing ? 'updated' : 'saved'} successfully!`);
        if (isEditing) {
          onSubmit()
        } else {
          handleCancel();
        }
      } else {
        alert.error(result.error || `Failed to ${isEditing ? 'update' : 'save'} assessment`);
      }
    } catch (error) {
      console.error(`Error ${isEditing ? 'updating' : 'saving'} assessment:`, error);
      alert.error(`An error occurred while ${isEditing ? 'updating' : 'saving'} assessment`);
    } finally {
      setLoading(false);
    }
  };

  const handleButtonClick = () => {
    if (!validateForm()) {
      return;
    }

    setShowPreview({
      show: true,
      showSubmitButton: true,
      onSubmit: handleSubmit,
    });
  };

  const handleNullValue = (value, fieldId = null) => {
    if (value === null || value === 'null') {
      return '';
    }
    return value || '';
  };

  const renderInputGroup = (label, field, type = 'text', options = null, placeholder = '', disabled = false) => {
    const value = field.includes('.')
      ? formData[field.split('.')[0]][field.split('.')[1]]
      : formData[field] || '';
    const isClearable = (field === 'built' || field === 'hand_dominance') ? false : options?.some(opt => opt.value === '');
    return (
      <div className="input-group-physio">
        <label>{label}</label>
        {type === 'select' ? (
          <Select
            value={options.find(option => option.value === value) || null}
            onChange={(selectedOption) => handleInputChange(field, selectedOption?.value || '')}
            options={options}
            placeholder="Select..."
            className="react-select-container"
            classNamePrefix="react-select"
            isClearable={isClearable}
            disabled={disabled}
          />
        ) : type === 'textarea' ? (
          <textarea
            value={value}
            onChange={(e) => handleInputChange(field, e.target.value)}
            placeholder={placeholder}
            rows="3"
            disabled={disabled}
          />
        ) : (
          <input
            type={type}
            value={handleNullValue(value)}
            onChange={(e) => handleInputChange(field, e.target.value)}
            placeholder={placeholder}
            disabled={disabled}
          />
        )}
      </div>
    );
  };

  const renderCheckboxGroup = (items) => (
    <div className="checkbox-group-inline">
      {items.map((item, index) => (
        <div key={index} className="checkbox-item">
          <span className="checkbox-text">{item.label}</span>
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={formData[item.field]}
              onChange={(e) => handleInputChange(item.field, e.target.checked)}
              className="checkbox-input"
            />
            <span className="checkbox-custom"></span>
          </label>
        </div>
      ))}
    </div>
  );

  const formatMuscleLabel = (muscle) => {
    return muscle.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase());
  };

  return (
    <>
      <div className="general-form-content">
        <div className="section-header">
          <h2>{isEditing ? 'EDIT NEURO ASSESSMENT FORM' : 'NEURO REHABILITATION PHYSIOTHERAPY ASSESSMENT FORM'}</h2>
          <IoPrintOutline
            style={{ marginLeft: 'auto', cursor: 'pointer', fontSize: '20px' }}
            onClick={() => setShowPreview({
              show: true,
              showSubmitButton: false,
              onSubmit: () => { handleSubmit() }
            })}
            title="Print Form"
          />
          {!isEditing && (show ? <LuShrink onClick={() => setShow(false)} style={{ marginLeft: 'auto', cursor: 'pointer' }} /> : <LuExpand onClick={() => setShow(true)} style={{ marginLeft: 'auto', cursor: 'pointer' }} />)}
        </div>
        {show && <>
          {/* Patient Information */}
          <div className="section patient-information">
            <div className="section-header">
              <LuUser className="section-icon" />
              <h3>Patient Information</h3>
            </div>
            <div className="input-row no3">
              {renderInputGroup('Patient ID', 'id', 'text', null, 'Patient ID...', true)}
              {renderInputGroup('Name', 'name', 'text', null, 'Patient name...', true)}
              {renderInputGroup('Age', 'age', 'number', null, 'Age...', true)}
            </div>
            <div className="input-row no3">
              {renderInputGroup('Gender', 'gender', 'text', null, 'Gender...', true)}
              {renderInputGroup('Contact Number', 'contact_number', 'tel', null, '', true)}
            </div>
          </div>

          {/* Chief Complaint */}
          <div className="section patient-information">
            <div className="section-header">
              <FiFileText className="section-icon" />
              <h3>Chief Complaint <span style={{ color: 'red' }}>*</span></h3>
            </div>

            {renderInputGroup('1. Weakness of UL/LL (Specify site and side)', 'chiefComplaint1', 'textarea')}
            {renderInputGroup('2. Difficulty in walking', 'chiefComplaint2', 'textarea')}
            {renderInputGroup('3. Difficulty in performing ADLs', 'chiefComplaint3', 'textarea')}
            {renderInputGroup('4. Pain present', 'chiefComplaint4', 'textarea')}
            {renderInputGroup('5. Any other', 'chiefComplaint5', 'textarea')}
          </div>

          {/* Clinical History */}
          <div className="section clinical-history">
            <div className="section-header">
              <FiFileText className="section-icon" />
              <h3>Clinical History</h3>
            </div>
            {renderInputGroup('Present History', 'history.present', 'textarea')}
            {renderInputGroup('Past History', 'history.past', 'textarea')}
            {renderInputGroup('Medical History', 'history.medical', 'textarea', null, 'DM / HTN / BA / Seizures/ Allergy/ (Specify if known)/Others')}
            {renderInputGroup('Surgical History', 'history.surgical', 'textarea')}
          </div>

          {/* General Examination */}
          <div className="section general-examination">
            <div className="section-header">
              <FiFileText className="section-icon" />
              <h3>General Examination</h3>
            </div>
            <div className="input-row no2">
              {renderInputGroup('Pulse rate (bpm)', 'general_examination.pulse', 'number')}
              {renderInputGroup('Blood pressure (mmHg)', 'general_examination.bp', 'text')}
            </div>
            <div className="input-row no2">
              {renderInputGroup('Built', 'built', 'select', [
                { value: 'thin', label: 'Thin (Endomorphic)' },
                { value: 'moderate', label: 'Moderate (Mesomorphic)' },
                { value: 'obese', label: 'Obese (Ectomorphic)' }
              ])}
              {renderInputGroup('Hand Dominance', 'hand_dominance', 'select', [
                { value: 'Right', label: 'Right' },
                { value: 'Left', label: 'Left' },
                { value: 'Ambidextrous', label: 'Ambidextrous' }
              ])}
            </div>
          </div>

          {/* CNS Examination */}
          <div className="section cns-examination">
            <div className="section-header">
              <FiFileText className="section-icon" />
              <h3>CNS Examination</h3>
            </div>

            <div className="checkbox-section">
              {renderCheckboxGroup([
                { field: 'conscious', label: 'Conscious' },
                { field: 'alert', label: 'Alert' },
                { field: 'obeys', label: 'Obeys' },
                { field: 'oriented', label: 'Oriented' }
              ])}
            </div>

            <div className="input-row no2">
              {renderInputGroup('Speech', 'speech', 'select', [
                { value: 'clear', label: 'Clear' },
                { value: 'comprehensible', label: 'Comprehensible' },
                { value: 'incomprehensible', label: 'Incomprehensible' },
                { value: 'aphasia', label: 'Aphasia' }
              ])}
            </div>
            {renderInputGroup('Cranial Nerve Examination (Specify if abnormal)', 'cne_examination', 'textarea')}
          </div>
          <div className="section deep-tendon-reflexes">
            <div className="section-header">
              <FiFileText className="section-icon" />
              <h3>Motor examination</h3>
            </div><div className="assessment-table">
              <table className="motor-examination-table">
                <thead>
                  <tr>
                    <th className="muscle-header" rowSpan="2" style={{ width: '150px' }}>MUSCLE</th>
                    <th className="assessment-header" colSpan="2">MMT</th>
                    <th className="assessment-header" colSpan="2">ROM</th>
                    <th className="assessment-header" colSpan="2">TONE</th>
                  </tr>
                  <tr>
                    <th className="side-header">RT</th>
                    <th className="side-header">LT</th>
                    <th className="side-header">RT</th>
                    <th className="side-header">LT</th>
                    <th className="side-header">RT</th>
                    <th className="side-header">LT</th>
                  </tr>
                </thead>
                <tbody>
                  {muscleGroups.map((muscle, index) => (
                    <tr key={index} className="motor-examination-row">
                      <td className="muscle-name-cell">
                        <strong>{muscle.label}</strong>
                      </td>
                      {/* MMT Columns */}
                      <td className="assessment-select-cell">
                        <input
                          type="text"
                          value={getMotorFunctionValue(muscle.label, 'RT', 'mmt')}
                          onChange={(e) => handleMotorFunctionChange(index, 'mmt', e.target.value, 'RT')}
                          className="table-input"
                        />
                      </td>
                      <td className="assessment-select-cell">
                        <input
                          type="text"
                          value={getMotorFunctionValue(muscle.label, 'LT', 'mmt')}
                          onChange={(e) => handleMotorFunctionChange(index, 'mmt', e.target.value, 'LT')}
                          className="table-input"
                        />
                      </td>
                      {/* ROM Columns */}
                      <td className="assessment-select-cell">
                        <input
                          type="text"
                          value={getMotorFunctionValue(muscle.label, 'RT', 'rom')}
                          onChange={(e) => handleMotorFunctionChange(index, 'rom', e.target.value, 'RT')}
                          className="table-input"
                        />
                      </td>
                      <td className="assessment-select-cell">
                        <input
                          type="text"
                          value={getMotorFunctionValue(muscle.label, 'LT', 'rom')}
                          onChange={(e) => handleMotorFunctionChange(index, 'rom', e.target.value, 'LT')}
                          className="table-input"
                        />
                      </td>
                      {/* Tone Columns */}
                      <td className="assessment-select-cell">
                        <input
                          type="text"
                          value={getMotorFunctionValue(muscle.label, 'RT', 'tone')}
                          onChange={(e) => handleMotorFunctionChange(index, 'tone', e.target.value, 'RT')}
                          className="table-input"
                        />
                      </td>
                      <td className="assessment-select-cell">
                        <input
                          type="text"
                          value={getMotorFunctionValue(muscle.label, 'LT', 'tone')}
                          onChange={(e) => handleMotorFunctionChange(index, 'tone', e.target.value, 'LT')}
                          className="table-input"
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          {/* Deep Tendon Reflexes */}
          <div className="section deep-tendon-reflexes">
            <div className="section-header">
              <FiFileText className="section-icon" />
              <h3>Deep Tendon Reflexes</h3>
            </div>
            <table className="tilt-table">
              <thead className="tilt-header">
                <tr>
                  <th>Reflex</th>
                  <th>Right</th>
                  <th>Left</th>
                </tr>
              </thead>
              <tbody>
                {['Biceps Jerk', 'Supinator Jerk', 'Triceps Jerk', 'Knee Jerk', 'Ankle Jerk'].map(reflexType => (
                  <tr key={reflexType} className="tilt-assessment-row">
                    <td className="category-cell">{reflexType}</td>
                    <td className="input-cell">
                      <input
                        type="text"
                        className="table-input"
                        value={formData.reflexes.find(r => r.reflex_type === reflexType && r.side === 'Right')?.response || ''}
                        onChange={(e) => {
                          let index = formData.reflexes.findIndex(r => r.reflex_type === reflexType && r.side === 'Right');
                          if (index === -1) {
                            setFormData(prev => ({
                              ...prev,
                              reflexes: [...prev.reflexes, { reflex_type: reflexType, side: 'Right', response: e.target.value }]
                            }));
                          } else {
                            handleReflexChange(index, 'response', e.target.value);
                          }
                        }}
                      />
                    </td>
                    <td className="input-cell">
                      <input
                        className="table-input"
                        type="text"
                        value={formData.reflexes.find(r => r.reflex_type === reflexType && r.side === 'Left')?.response || ''}
                        onChange={(e) => {
                          let index = formData.reflexes.findIndex(r => r.reflex_type === reflexType && r.side === 'Left');
                          if (index === -1) {
                            setFormData(prev => ({
                              ...prev,
                              reflexes: [...prev.reflexes, { reflex_type: reflexType, side: 'Left', response: e.target.value }]
                            }));
                          } else {
                            handleReflexChange(index, 'response', e.target.value);
                          }
                        }}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="checkbox-section">
              {renderCheckboxGroup([
                { field: 'babinski', label: 'Babinski Sign' }
              ])}
              {formData.babinski && renderInputGroup('Specify Details', 'babinski_details', 'textarea')}
            </div>
          </div>
          {/* Sensory Examination */}
          <div className="section sensory-examination">
            <div className="section-header">
              <FiFileText className="section-icon" />
              <h3>Sensory Examination</h3>
            </div>

            <h4>Superficial Sensations</h4>
            <table className="tilt-table">
              <thead className="tilt-header">
                <tr>
                  <th>Sensation</th>
                  <th>RT</th>
                  <th>LT</th>
                </tr>
              </thead>
              <tbody>
                {Object.keys(formData.sensory.superficial || {}).map(sensation => (
                  <tr key={sensation} className="tilt-assessment-row">
                    <td className="category-cell">{formatMuscleLabel(sensation)}</td>
                    <td className="input-cell">
                      <input
                        className="table-input"
                        type="text"
                        value={formData.sensory.superficial[sensation].RT}
                        onChange={(e) => handleNestedChange('sensory', 'superficial', sensation, { ...formData.sensory.superficial[sensation], RT: e.target.value })}
                      />
                    </td>
                    <td className="input-cell">
                      <input
                        className="table-input"
                        type="text"
                        value={formData.sensory.superficial[sensation].LT}
                        onChange={(e) => handleNestedChange('sensory', 'superficial', sensation, { ...formData.sensory.superficial[sensation], LT: e.target.value })}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <h4>Deep Sensations</h4>
            <table className="tilt-table">
              <thead className="tilt-header">
                <tr>
                  <th>Sensation</th>
                  <th>RT</th>
                  <th>LT</th>
                </tr>
              </thead>
              <tbody>
                {Object.keys(formData.sensory.deep || {}).map(sensation => (
                  <tr key={sensation} className="tilt-assessment-row">
                    <td className="category-cell">{formatMuscleLabel(sensation)}</td>
                    <td className="input-cell">
                      <input
                        className="table-input"
                        type="text"
                        value={formData.sensory.deep[sensation].RT}
                        onChange={(e) => handleNestedChange('sensory', 'deep', sensation, { ...formData.sensory.deep[sensation], RT: e.target.value })}
                      />
                    </td>
                    <td className="input-cell">
                      <input
                        className="table-input"
                        type="text"
                        value={formData.sensory.deep[sensation].LT}
                        onChange={(e) => handleNestedChange('sensory', 'deep', sensation, { ...formData.sensory.deep[sensation], LT: e.target.value })}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <h4>Cortical Sensations</h4>
            <table className="tilt-table">
              <thead className="tilt-header">
                <tr>
                  <th>Sensation</th>
                  <th>RT</th>
                  <th>LT</th>
                </tr>
              </thead>
              <tbody>
                {Object.keys(formData.sensory.cortical || {}).map(sensation => (
                  <tr key={sensation} className="tilt-assessment-row">
                    <td className="category-cell">{formatMuscleLabel(sensation)}</td>
                    <td className="input-cell">
                      <input
                        className="table-input"
                        type="text"
                        value={formData.sensory.cortical[sensation].RT}
                        onChange={(e) => handleNestedChange('sensory', 'cortical', sensation, { ...formData.sensory.cortical[sensation], RT: e.target.value })}
                      />
                    </td>
                    <td className="input-cell">
                      <input
                        className="table-input"
                        type="text"
                        value={formData.sensory.cortical[sensation].LT}
                        onChange={(e) => handleNestedChange('sensory', 'cortical', sensation, { ...formData.sensory.cortical[sensation], LT: e.target.value })}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Coordination */}
          <div className="section additional-assessments">
            <div className="section-header">
              <FiFileText className="section-icon" />
              <h3>Coordination</h3>
            </div>
            <div className="input-row no2">
              {renderInputGroup('Finger to nose', 'coordination.finger_to_nose', 'textarea')}
              {renderInputGroup('Finger opposition', 'coordination.finger_opposition', 'textarea')}
            </div>
            <div className="input-row no2">
              {renderInputGroup('Mass grasp', 'coordination.mass_grasp', 'textarea')}
              {renderInputGroup('Pronation/Supination', 'coordination.pronation_supination', 'textarea')}
            </div>
            <div className="input-row no2">
              {renderInputGroup('Rebound test', 'coordination.rebound_test', 'textarea')}
              {renderInputGroup('Tapping (Hand)', 'coordination.tapping_hand', 'textarea')}
            </div>
            <div className="input-row no2">
              {renderInputGroup('Tapping (Foot)', 'coordination.tapping_foot', 'textarea')}
              {renderInputGroup('Heel to knee', 'coordination.heel_to_knee', 'textarea')}
            </div>
            <div className="input-row no2">
              {renderInputGroup('Drawing a circle (Hand)', 'coordination.drawing_circle_hand', 'textarea')}
              {renderInputGroup('Drawing a circle (Foot)', 'coordination.drawing_circle_foot', 'textarea')}
            </div>
          </div>

          {/* Balance and Other Assessments */}
          <div className="section additional-assessments">
            <div className="section-header">
              <FiFileText className="section-icon" />
              <h3>Additional Assessments</h3>
            </div>

            <h4>Balance</h4>
            {renderInputGroup('Sitting', 'balance.sitting', 'textarea')}
            {renderInputGroup('Standing', 'balance.standing', 'textarea')}
            {renderInputGroup('Posture', 'posture', 'textarea')}
            {renderInputGroup('Gait', 'gait', 'textarea')}
            {renderInputGroup('Functional Diagnosis', 'functional_diagnosis', 'textarea')}
            {renderInputGroup('Treatment Goal', 'treatment_goal', 'textarea')}
            <h4>Brunnstrom Stages</h4>
            <Brunnstrom />
            <div className="input-row no2">
              {renderInputGroup('Admission', 'brunnstorm.admission', 'select', [
                { value: '1', label: 'Stage 1' },
                { value: '2', label: 'Stage 2' },
                { value: '3', label: 'Stage 3' },
                { value: '4', label: 'Stage 4' },
                { value: '5', label: 'Stage 5' },
                { value: '6', label: 'Stage 6' }
              ])}
              {renderInputGroup('Discharge', 'brunnstorm.discharge', 'select', [
                { value: '1', label: 'Stage 1' },
                { value: '2', label: 'Stage 2' },
                { value: '3', label: 'Stage 3' },
                { value: '4', label: 'Stage 4' },
                { value: '5', label: 'Stage 5' },
                { value: '6', label: 'Stage 6' }
              ])}
            </div>

            <h4>Pain Rating</h4>
            <PainScale />
            <div className="input-row no2">
              {renderInputGroup('Admission Pain Rating (0-10)', 'pain_rating.admission', 'number')}
              {renderInputGroup('Discharge Pain Rating (0-10)', 'pain_rating.discharge', 'number')}
            </div>
          </div>

          {/* Form Actions */}
          <div className="general-form-actions no-print">
            <button type="button" disabled={loading} onClick={handleCancel} className="cancel-btn">
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              onClick={handleButtonClick}
              className="submit-btn"
              style={{ cursor: loading && 'wait' }}
            >
              {loading ? (isEditing ? 'Updating...' : 'Saving...') : (isEditing ? 'Update' : 'Save')}
            </button>
          </div>
        </>}
      </div>
      {!isEditing && <NewScoring
        refresh={refresh}
        data={data}
      />}
      {showPreview.show && (
        <AssessmentPreview
          formData={formData}
          handleSubmit={showPreview.onSubmit}
          formType='neuro'
          onClose={() => setShowPreview({ show: false })}
          showSubmitButton={showPreview.showSubmitButton}
        />
      )}
    </>

  );
};

export default NeuroAssessment;